/**
 * EventsPage - NLT Radio Events + Photo Galleries
 * A global photo feed on top + browsable event albums, searchable by zip code.
 * Owner + Artists create events; any authenticated patron can upload photos.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
  Calendar, MapPin, Plus, Search, Heart, X, ImagePlus,
  Users, ImageIcon, Grid3x3, Loader2, Trash2, Check, CalendarCheck,
  Navigation, Bell, SlidersHorizontal, FolderPlus, Download, Share2,
  MessageCircle, Send, Play, Star, Video, Clapperboard
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import Footer from '../components/Footer';
import NLTWatermark from '../components/NLTWatermark';

const APP_ORIGIN = 'https://nltradio.com';
const REACTIONS = ['❤️', '🔥', '😂', '😮', '👏'];

const totalReactions = (reactions) => Object.values(reactions || {}).reduce((s, arr) => s + (arr?.length || 0), 0);

// Resize/compress an image in the browser before upload so huge phone photos
// (often 8-15MB) don't get rejected by size/proxy limits and always reach R2.
const compressImage = (file, maxDim = 2048, quality = 0.85) => new Promise((resolve) => {
  if (!file || !file.type || !file.type.startsWith('image/')) { resolve(file); return; }
  const url = URL.createObjectURL(file);
  const img = new window.Image();
  img.onload = () => {
    URL.revokeObjectURL(url);
    let { width, height } = img;
    if (width > maxDim || height > maxDim) {
      if (width >= height) { height = Math.round((height * maxDim) / width); width = maxDim; }
      else { width = Math.round((width * maxDim) / height); height = maxDim; }
    }
    try {
      const canvas = document.createElement('canvas');
      canvas.width = width; canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        if (!blob) { resolve(file); return; }
        const name = (file.name || 'photo').replace(/\.[^.]+$/, '') + '.jpg';
        const out = new File([blob], name, { type: 'image/jpeg' });
        resolve(out.size < file.size ? out : file);
      }, 'image/jpeg', quality);
    } catch { resolve(file); }
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

// Capture a poster frame from a video File in the browser (best-effort, returns a JPEG Blob or null).
const generateVideoPoster = (file) => new Promise((resolve) => {
  try {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    const url = URL.createObjectURL(file);
    video.src = url;
    let settled = false;
    const finish = (blob) => { if (!settled) { settled = true; URL.revokeObjectURL(url); resolve(blob); } };
    video.onloadedmetadata = () => {
      const t = Math.min(1, (video.duration || 2) / 2);
      try { video.currentTime = t; } catch (e) { finish(null); }
    };
    video.onseeked = () => {
      try {
        const w = video.videoWidth, h = video.videoHeight;
        if (!w || !h) return finish(null);
        const maxDim = 720;
        const scale = Math.max(w, h) > maxDim ? maxDim / Math.max(w, h) : 1;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(w * scale);
        canvas.height = Math.round(h * scale);
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob((b) => finish(b), 'image/jpeg', 0.8);
      } catch (e) { finish(null); }
    };
    video.onerror = () => finish(null);
    setTimeout(() => finish(null), 8000);
  } catch (e) { resolve(null); }
});

// Grid thumbnail for gallery media. For videos without a server/poster thumbnail,
// render the actual video element (metadata only) so a real frame shows instead of the logo.
const MediaThumb = ({ p, isVid }) => {
  const media = (isVid && !p.thumbnail_url) ? (
    <video
      src={p.photo_url}
      preload="metadata"
      muted
      playsInline
      className="w-full h-full object-cover group-hover:scale-105 transition-transform pointer-events-none"
    />
  ) : (
    <img
      src={isVid ? p.thumbnail_url : p.photo_url}
      alt={p.caption || 'media'}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform"
    />
  );
  return (
    <>
      {media}
      <NLTWatermark size="xs" />
    </>
  );
};


const sharePhoto = async (photo) => {
  const shareUrl = `${APP_ORIGIN}/api/radio/og/photo/${photo.id}`;
  const shareData = {
    title: photo.event_title || 'NLT Radio',
    text: photo.caption || 'Check out this event photo on NLT Radio',
    url: shareUrl,
  };
  try {
    if (navigator.share) {
      await navigator.share(shareData);
      return;
    }
  } catch { /* user cancelled or unsupported — fall through to copy */ }
  try {
    await navigator.clipboard.writeText(shareUrl);
    toast.success('Share link copied to clipboard!');
  } catch {
    toast.error('Could not copy link');
  }
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const formatDate = (d) => {
  if (!d) return '';
  const date = new Date(d);
  if (isNaN(date.getTime())) return d;
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// ============ Create Event Modal ============
const CreateEventModal = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({ title: '', zipCode: '', city: '', state: '', locationText: '', eventDate: '', description: '', category: '' });
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (k, v) => { setForm(prev => ({ ...prev, [k]: v })); if (error) setError(''); };

  const handleCover = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setCoverFile(f);
    setCoverPreview(URL.createObjectURL(f));
  };

  const submit = async () => {
    setError('');
    if (!form.title.trim()) return setError('Event title is required');
    if (!form.zipCode.trim()) return setError('Zip code is required');
    setSaving(true);
    try {
      let imageUrl = '';
      if (coverFile) {
        const fd = new FormData();
        fd.append('file', coverFile);
        const up = await axios.post(`${API_URL}/api/events/cover-upload`, fd, { headers: authHeaders() });
        imageUrl = up.data?.url || '';
      }
      const res = await axios.post(`${API_URL}/api/events`, { ...form, imageUrl }, { headers: authHeaders() });
      onCreated(res.data);
      setForm({ title: '', zipCode: '', city: '', state: '', locationText: '', eventDate: '', description: '', category: '' });
      setCoverFile(null); setCoverPreview(null);
      onClose();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create event');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 backdrop-blur-md z-[80]" onClick={onClose} />
          <div className="fixed inset-0 z-[81] flex items-center justify-center p-4 overflow-y-auto">
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="w-full max-w-lg bg-[#12101A] rounded-3xl border border-white/10 my-8" data-testid="create-event-modal">
              <div className="flex items-center justify-between p-6 border-b border-white/10">
                <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Host an Event</h2>
                <button onClick={onClose} type="button" data-testid="close-create-event" className="p-2 rounded-full hover:bg-white/10">
                  <X className="w-5 h-5 text-white/70" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <label className="block cursor-pointer">
                  <div className="aspect-[16/9] rounded-2xl border-2 border-dashed border-white/15 overflow-hidden flex items-center justify-center bg-white/5 hover:border-[#00F0FF]/50 transition-colors">
                    {coverPreview ? (
                      <img src={coverPreview} alt="cover" className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-center text-white/50">
                        <ImagePlus className="w-8 h-8 mx-auto mb-1" />
                        <span className="text-sm">Add cover photo</span>
                      </div>
                    )}
                  </div>
                  <input type="file" accept="image/*" className="hidden" onChange={handleCover} data-testid="event-cover-input" />
                </label>

                <input value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="Event title *"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-[#00F0FF]/50 outline-none" data-testid="event-title-input" />

                <div className="grid grid-cols-2 gap-3">
                  <input value={form.zipCode} onChange={(e) => set('zipCode', e.target.value)} placeholder="Zip code *" inputMode="numeric"
                    className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-[#00F0FF]/50 outline-none" data-testid="event-zip-input" />
                  <input type="date" value={form.eventDate} onChange={(e) => set('eventDate', e.target.value)}
                    className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-[#00F0FF]/50 outline-none" data-testid="event-date-input" />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <input value={form.city} onChange={(e) => set('city', e.target.value)} placeholder="City"
                    className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-[#00F0FF]/50 outline-none" data-testid="event-city-input" />
                  <input value={form.state} onChange={(e) => set('state', e.target.value)} placeholder="State"
                    className="px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-[#00F0FF]/50 outline-none" data-testid="event-state-input" />
                </div>

                <input value={form.locationText} onChange={(e) => set('locationText', e.target.value)} placeholder="Venue / location name"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-[#00F0FF]/50 outline-none" data-testid="event-location-input" />

                <select value={form.category} onChange={(e) => set('category', e.target.value)} data-testid="event-category-input"
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white focus:border-[#00F0FF]/50 outline-none">
                  <option value="" className="bg-[#12101A]">Category (optional)</option>
                  {['Music', 'Nightlife', 'Show', 'Festival', 'Community', 'Other'].map(c => <option key={c} value={c} className="bg-[#12101A]">{c}</option>)}
                </select>

                <textarea value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="Describe the event..." rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-white/40 focus:border-[#00F0FF]/50 outline-none resize-none" data-testid="event-description-input" />

                {error && <p className="text-[#FF007F] text-sm" data-testid="create-event-error">{error}</p>}
              </div>
              <div className="p-6 pt-0">
                <button onClick={submit} type="button" disabled={saving} data-testid="submit-create-event"
                  className="w-full py-3.5 rounded-xl font-semibold bg-[#00F0FF] text-[#050505] hover:bg-[#00F0FF]/90 disabled:opacity-60 flex items-center justify-center gap-2 transition-colors">
                  {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Publish Event'}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

// ============ Media Viewer (photo/video + reactions + comments) ============
const PhotoViewer = ({ photo, onClose, currentUser, canManage, onDeleted, onReacted }) => {
  const [mediaDoc, setMediaDoc] = useState(photo);
  const [reactions, setReactions] = useState(photo?.reactions || {});
  const [comments, setComments] = useState([]);
  const [loadingC, setLoadingC] = useState(true);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const isVideo = mediaDoc?.media_type === 'video';
  const processing = isVideo && mediaDoc?.status && mediaDoc.status !== 'ready';
  const myReaction = Object.keys(reactions).find(r => (reactions[r] || []).includes(currentUser?.id));

  useEffect(() => { setMediaDoc(photo); setReactions(photo?.reactions || {}); }, [photo]);

  // Poll transcode status so a freshly uploaded video appears without reopening
  useEffect(() => {
    if (!photo?.id || mediaDoc?.media_type !== 'video' || mediaDoc?.status === 'ready') return;
    const iv = setInterval(async () => {
      try {
        const res = await axios.get(`${API_URL}/api/events/gallery/media/${photo.id}`);
        const m = res.data?.media;
        if (m && m.status === 'ready') { setMediaDoc(m); clearInterval(iv); }
      } catch { /* keep polling */ }
    }, 3000);
    return () => clearInterval(iv);
  }, [photo?.id, mediaDoc?.media_type, mediaDoc?.status]);

  useEffect(() => {
    if (!photo?.id) return;
    setLoadingC(true);
    axios.get(`${API_URL}/api/events/gallery/${photo.id}/comments`)
      .then(r => setComments(r.data?.comments || [])).catch(() => {}).finally(() => setLoadingC(false));
  }, [photo?.id]);

  if (!photo) return null;

  const react = async (emoji) => {
    if (!currentUser) { toast.error('Sign in to react'); return; }
    try {
      const res = await axios.post(`${API_URL}/api/events/${photo.event_id}/gallery/${photo.id}/react`, { reaction: emoji }, { headers: authHeaders() });
      setReactions(res.data?.reactions || {});
      onReacted && onReacted(photo.id, res.data?.reactions || {});
    } catch { toast.error('Could not react'); }
  };

  const addComment = async () => {
    const t = text.trim();
    if (!t) return;
    if (!currentUser) { toast.error('Sign in to comment'); return; }
    setPosting(true);
    try {
      const res = await axios.post(`${API_URL}/api/events/gallery/${photo.id}/comments`, { text: t }, { headers: authHeaders() });
      setComments(prev => [...prev, res.data.comment]);
      setText('');
    } catch { toast.error('Could not post comment'); }
    setPosting(false);
  };

  const delComment = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/events/gallery/comments/${id}`, { headers: authHeaders() });
      setComments(prev => prev.filter(c => c.id !== id));
    } catch { toast.error('Could not delete comment'); }
  };

  const delMedia = async () => {
    if (!window.confirm('Delete this from the gallery? This cannot be undone.')) return;
    try {
      await axios.delete(`${API_URL}/api/events/${photo.event_id}/gallery/${photo.id}`, { headers: authHeaders() });
      onDeleted && onDeleted(photo.id);
      onClose();
      toast.success('Removed from gallery');
    } catch { toast.error('Could not delete'); }
  };

  const downloadMedia = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(`${API_URL}/api/events/gallery/${photo.id}/download`);
      if (!res.ok) throw new Error('download failed');
      const blob = await res.blob();
      const isVid = photo.media_type === 'video';
      const ext = isVid ? 'mp4' : 'jpg';
      const mime = blob.type || (isVid ? 'video/mp4' : 'image/jpeg');
      const base = `nltradio_${(photo.event_title || 'event').replace(/\s+/g, '_').slice(0, 40)}_${(photo.id || '').slice(0, 8)}`;
      const fname = `${base}.${ext}`;
      const file = new File([blob], fname, { type: mime });

      // Preferred on mobile: hand the file to the native Share Sheet. The user taps
      // "Save Image" / "Save Video" and it goes straight to their Photos/gallery —
      // the only way a website can get media into the camera roll (OS-sandboxed).
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({ files: [file] });
          setDownloading(false);
          return;
        } catch (err) {
          if (err && err.name === 'AbortError') { setDownloading(false); return; } // user cancelled
          // any other share error -> fall through to a normal file download
        }
      }

      // Fallback (desktop / browsers without file-share): save to the device.
      const objUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objUrl;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(objUrl), 5000);
    } catch {
      toast.error('Could not download. Please try again.');
    }
    setDownloading(false);
  };


  return createPortal(
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-[90] bg-black/95 flex flex-col md:flex-row" data-testid="photo-viewer">
        <button onClick={onClose} className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 z-20" data-testid="close-photo-viewer">
          <X className="w-6 h-6 text-white" />
        </button>

        {/* Media area */}
        <div className="flex-1 flex items-center justify-center p-4 min-h-0">
          {processing ? (
            <div className="text-center text-white/70" data-testid="media-processing">
              <Loader2 className="w-10 h-10 mx-auto mb-3 animate-spin text-[#00F0FF]" />
              <p className="text-sm">Processing video… it'll play here in a moment.</p>
            </div>
          ) : isVideo ? (
            <div className="relative inline-block">
              <video src={mediaDoc.photo_url} poster={mediaDoc.thumbnail_url || undefined} controls playsInline autoPlay
                className="max-h-[80vh] max-w-full rounded-2xl" data-testid="media-video" />
              <NLTWatermark size="md" />
            </div>
          ) : (
            <div className="relative inline-block">
              <img src={mediaDoc.photo_url} alt={mediaDoc.caption || 'event photo'} className="max-h-[80vh] max-w-full rounded-2xl object-contain" />
              <NLTWatermark size="md" />
            </div>
          )}
        </div>

        {/* Side / bottom panel */}
        <div className="w-full md:w-[380px] flex-shrink-0 bg-[#0B0A10] md:border-l border-t md:border-t-0 border-white/10 flex flex-col max-h-[45vh] md:max-h-none">
          <div className="p-4 border-b border-white/10">
            {photo.event_title && <p className="text-[#00F0FF] text-xs font-medium">{photo.event_title}</p>}
            {photo.caption && <p className="text-white text-sm mt-1">{photo.caption}</p>}
            <p className="text-white/40 text-xs mt-1">by {photo.user_name || 'Guest'}</p>

            {/* Reaction bar */}
            <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid="reaction-bar">
              {REACTIONS.map(emoji => {
                const count = (reactions[emoji] || []).length;
                const mine = myReaction === emoji;
                return (
                  <button key={emoji} onClick={() => react(emoji)} data-testid={`react-${emoji}`}
                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full text-sm transition-colors ${mine ? 'bg-[#FF007F]/25 ring-1 ring-[#FF007F]' : 'bg-white/10 hover:bg-white/20'}`}>
                    <span className="text-base leading-none">{emoji}</span>
                    {count > 0 && <span className="text-white text-xs">{count}</span>}
                  </button>
                );
              })}
            </div>

            {/* Actions */}
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <button onClick={downloadMedia} disabled={downloading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white/10 text-white hover:bg-white/20 transition-colors disabled:opacity-50"
                data-testid="photo-viewer-download">
                <Download className="w-3.5 h-3.5" /> {downloading ? 'Saving…' : 'Save to Photos'}
              </button>
              <button onClick={() => sharePhoto(photo)} data-testid="photo-viewer-share"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#00F0FF]/15 text-[#00F0FF] hover:bg-[#00F0FF]/25 transition-colors">
                <Share2 className="w-3.5 h-3.5" /> Share
              </button>
              {canManage && (
                <button onClick={delMedia} data-testid="media-delete-btn"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-[#FF007F]/15 text-[#FF007F] hover:bg-[#FF007F]/25 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              )}
            </div>
          </div>

          {/* Comments */}
          <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="media-comments">
            <p className="text-white/50 text-xs font-medium flex items-center gap-1.5"><MessageCircle className="w-3.5 h-3.5" /> {comments.length} Comment{comments.length !== 1 ? 's' : ''}</p>
            {loadingC ? (
              <div className="py-4 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#00F0FF]" /></div>
            ) : comments.length === 0 ? (
              <p className="text-white/30 text-sm">Be the first to comment.</p>
            ) : comments.map(c => (
              <div key={c.id} className="flex items-start gap-2 group" data-testid={`comment-${c.id}`}>
                <div className="w-7 h-7 rounded-full bg-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center">
                  {c.user_photo ? <img src={c.user_photo} alt="" className="w-full h-full object-cover" /> : <Users className="w-3.5 h-3.5 text-white/50" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm"><span className="font-medium">{c.user_name}</span> <span className="text-white/80">{c.text}</span></p>
                </div>
                {(canManage || c.user_id === currentUser?.id) && (
                  <button onClick={() => delComment(c.id)} data-testid={`delete-comment-${c.id}`}
                    className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-[#FF007F] transition-opacity">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            ))}
          </div>

          {/* Comment input */}
          {currentUser && (
            <div className="p-3 border-t border-white/10 flex items-center gap-2">
              <input value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addComment()}
                placeholder="Add a comment…" data-testid="comment-input"
                className="flex-1 px-3 py-2 rounded-full bg-white/5 border border-white/10 text-white text-sm placeholder-white/40 outline-none focus:border-[#00F0FF]/50" />
              <button onClick={addComment} disabled={posting || !text.trim()} data-testid="comment-submit"
                className="p-2 rounded-full bg-[#00F0FF] text-[#050505] disabled:opacity-40 hover:bg-[#00F0FF]/90 transition-colors">
                {posting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

// ============ Event Reviews Section ============
const StarRow = ({ value, onChange, size = 'w-5 h-5' }) => (
  <div className="inline-flex items-center gap-0.5">
    {[1, 2, 3, 4, 5].map(n => (
      <button key={n} type={onChange ? 'button' : undefined} disabled={!onChange}
        onClick={onChange ? () => onChange(n) : undefined} data-testid={onChange ? `star-${n}` : undefined}
        className={onChange ? 'transition-transform hover:scale-110' : 'cursor-default'}>
        <Star className={`${size} ${n <= value ? 'text-[#FFD400] fill-current' : 'text-white/25'}`} />
      </button>
    ))}
  </div>
);

const ReviewsSection = ({ event, currentUser, isAttending, canManage }) => {
  const [reviews, setReviews] = useState([]);
  const [avg, setAvg] = useState(0);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const canReview = currentUser && (isAttending || canManage);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/events/${event.id}/reviews`);
      setReviews(res.data?.reviews || []);
      setAvg(res.data?.average_rating || 0);
      const mine = (res.data?.reviews || []).find(r => r.user_id === currentUser?.id);
      if (mine) { setRating(mine.rating); setText(mine.text || ''); }
    } catch { /* noop */ }
  }, [event.id, currentUser?.id]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!rating) { toast.error('Pick a star rating'); return; }
    setSaving(true);
    try {
      await axios.post(`${API_URL}/api/events/${event.id}/reviews`, { rating, text: text.trim() }, { headers: authHeaders() });
      toast.success('Review posted!');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not post review');
    }
    setSaving(false);
  };

  const del = async (id) => {
    try { await axios.delete(`${API_URL}/api/events/reviews/${id}`, { headers: authHeaders() }); await load(); }
    catch { toast.error('Could not delete review'); }
  };

  return (
    <div className="mt-6" data-testid="reviews-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-semibold flex items-center gap-2"><Star className="w-5 h-5 text-[#FFD400]" /> Reviews ({reviews.length})</h3>
        {reviews.length > 0 && (
          <div className="flex items-center gap-2" data-testid="reviews-average">
            <StarRow value={Math.round(avg)} size="w-4 h-4" />
            <span className="text-white text-sm font-medium">{avg.toFixed(1)}</span>
          </div>
        )}
      </div>

      {canReview ? (
        <div className="bg-white/5 rounded-2xl p-4 mb-4 border border-white/5" data-testid="review-form">
          <StarRow value={rating} onChange={setRating} />
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={2} placeholder="Share your experience…"
            className="w-full mt-3 px-3 py-2 rounded-xl bg-white/5 border border-white/10 text-white text-sm placeholder-white/40 outline-none focus:border-[#00F0FF]/50 resize-none" data-testid="review-text" />
          <button onClick={submit} disabled={saving} data-testid="submit-review"
            className="mt-2 px-4 py-2 rounded-full bg-[#FFD400] text-[#050505] text-sm font-semibold hover:bg-[#FFD400]/90 disabled:opacity-60 inline-flex items-center gap-1.5 transition-colors">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />} Post Review
          </button>
        </div>
      ) : currentUser ? (
        <p className="text-white/40 text-sm mb-3">RSVP as attending to leave a review.</p>
      ) : null}

      <div className="space-y-3">
        {reviews.length === 0 ? (
          <p className="text-white/30 text-sm">No reviews yet.</p>
        ) : reviews.map(r => (
          <div key={r.id} className="flex items-start gap-2 group" data-testid={`review-${r.id}`}>
            <div className="w-8 h-8 rounded-full bg-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center">
              {r.user_photo ? <img src={r.user_photo} alt="" className="w-full h-full object-cover" /> : <Users className="w-4 h-4 text-white/50" />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-white text-sm font-medium">{r.user_name}</span>
                <StarRow value={r.rating} size="w-3 h-3" />
              </div>
              {r.text && <p className="text-white/80 text-sm mt-0.5">{r.text}</p>}
            </div>
            {(canManage || r.user_id === currentUser?.id) && (
              <button onClick={() => del(r.id)} data-testid={`delete-review-${r.id}`}
                className="opacity-0 group-hover:opacity-100 text-white/30 hover:text-[#FF007F] transition-opacity">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ============ Event Detail Modal ============
const EventDetailModal = ({ event, onClose, onOpenPhoto, currentUser, onDeleted, onRsvpChange }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [rsvpIds, setRsvpIds] = useState(event.rsvpUserIds || []);
  const [rsvpBusy, setRsvpBusy] = useState(false);
  const [albums, setAlbums] = useState(event.albums || []);
  const [activeAlbum, setActiveAlbum] = useState('all'); // 'all' | album name
  const [mediaFilter, setMediaFilter] = useState('all'); // 'all' | 'image' | 'video'
  const [videoProgress, setVideoProgress] = useState(null); // {index,total,pct}
  const [uploadAlbum, setUploadAlbum] = useState('');     // album to upload into
  const [newAlbum, setNewAlbum] = useState('');
  const [notifying, setNotifying] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState('');
  const isOwnerOrCreator = currentUser && (event.userId === currentUser.id || currentUser.role === 'super_admin' || currentUser.isSuperAdmin);
  const isGoing = currentUser && rsvpIds.includes(currentUser.id);
  const filteredMedia = photos.filter(p =>
    (activeAlbum === 'all' || (p.album || '') === activeAlbum) &&
    (mediaFilter === 'all' || (p.media_type || 'image') === mediaFilter)
  );

  const addAlbum = async () => {
    const name = newAlbum.trim();
    if (!name) return;
    try {
      const res = await axios.post(`${API_URL}/api/events/${event.id}/albums`, { name }, { headers: authHeaders() });
      setAlbums(res.data?.albums || [...albums, name]);
      setNewAlbum('');
      setUploadAlbum(name);
    } catch { /* noop */ }
  };

  const notifyAttendees = async () => {
    setNotifying(true);
    setNotifyMsg('');
    try {
      const res = await axios.post(`${API_URL}/api/events/${event.id}/notify-attendees`, {}, { headers: authHeaders() });
      setNotifyMsg(res.data?.notified ? `Notified ${res.data.notified} attendee${res.data.notified > 1 ? 's' : ''}! 📣` : 'No attendees to notify yet.');
    } catch {
      setNotifyMsg('Could not send notifications.');
    }
    setNotifying(false);
  };

  const toggleRsvp = async () => {
    if (!currentUser || rsvpBusy) return;
    setRsvpBusy(true);
    // optimistic update
    const next = isGoing ? rsvpIds.filter(id => id !== currentUser.id) : [...rsvpIds, currentUser.id];
    setRsvpIds(next);
    try {
      const res = await axios.post(`${API_URL}/api/events/${event.id}/rsvp`, {}, { headers: authHeaders() });
      if (typeof res.data?.rsvpCount === 'number' && onRsvpChange) onRsvpChange(event.id, next);
    } catch {
      setRsvpIds(rsvpIds); // revert on error
    }
    setRsvpBusy(false);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/events/${event.id}/gallery`);
      setPhotos(res.data?.photos || []);
    } catch { /* noop */ }
    setLoading(false);
  }, [event.id]);

  useEffect(() => { load(); }, [load]);

  const uploadVideo = async (file, onProgress) => {
    // Direct-to-R2 presigned upload — same fast path as Clips. Bytes go straight
    // from the browser to storage (never through the app proxy), so uploads are
    // fast and the video is playable instantly.
    const contentType = file.type || 'video/mp4';

    // Generate a poster frame in the browser and upload it to R2 first, so the
    // gallery shows a REAL video frame immediately (no waiting on / relying on the
    // server transcode). Best-effort — falls back to server thumbnail if this fails.
    let thumbnailKey = null;
    try {
      const thumbBlob = await generateVideoPoster(file);
      if (thumbBlob) {
        const tPre = await axios.get(`${API_URL}/api/events/${event.id}/gallery/presigned-upload`, {
          params: { filename: 'poster.jpg', content_type: 'image/jpeg' },
          headers: authHeaders(),
        });
        await axios.put(tPre.data.upload_url, thumbBlob, { headers: { 'Content-Type': 'image/jpeg' } });
        thumbnailKey = tPre.data.r2_key;
      }
    } catch (e) { /* poster is best-effort */ }

    const presign = await axios.get(`${API_URL}/api/events/${event.id}/gallery/presigned-upload`, {
      params: { filename: file.name || 'video.mp4', content_type: contentType },
      headers: authHeaders(),
    });
    const { upload_url, media_id, r2_key } = presign.data;

    // PUT directly to R2. Do NOT send app auth headers here (breaks the R2 signature).
    await axios.put(upload_url, file, {
      headers: { 'Content-Type': contentType },
      onUploadProgress: (evt) => {
        if (onProgress && evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100));
      },
    });

    const done = new FormData();
    done.append('media_id', media_id);
    done.append('r2_key', r2_key);
    done.append('content_type', contentType);
    if (uploadAlbum) done.append('album', uploadAlbum);
    if (thumbnailKey) done.append('thumbnail_key', thumbnailKey);
    await axios.post(`${API_URL}/api/events/${event.id}/gallery/confirm`, done, { headers: authHeaders() });
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    const images = files.filter(f => (f.type || '').startsWith('image/'));
    const videos = files.filter(f => (f.type || '').startsWith('video/'));
    const MAX_VID = 2 * 1024 * 1024 * 1024; // 2GB
    const tooBig = videos.filter(f => f.size > MAX_VID);
    const okVideos = videos.filter(f => f.size <= MAX_VID);
    try {
      let added = 0, failed = 0;
      if (images.length) {
        const processed = await Promise.all(images.map(f => compressImage(f)));
        const fd = new FormData();
        processed.forEach(f => fd.append('files', f));
        if (uploadAlbum) fd.append('album', uploadAlbum);
        const res = await axios.post(`${API_URL}/api/events/${event.id}/gallery/batch`, fd, { headers: authHeaders() });
        added += res.data?.uploaded || 0;
        failed += res.data?.failed || 0;
      }
      for (let vi = 0; vi < okVideos.length; vi++) {
        const v = okVideos[vi];
        try {
          setVideoProgress({ index: vi + 1, total: okVideos.length, pct: 0 });
          await uploadVideo(v, (pct) => setVideoProgress({ index: vi + 1, total: okVideos.length, pct }));
          added += 1;
        } catch { failed += 1; }
      }
      setVideoProgress(null);
      if (tooBig.length) toast.error(`${tooBig.length} video${tooBig.length > 1 ? 's' : ''} over 2GB skipped`);
      if (added) toast.success(`${added} item${added > 1 ? 's' : ''} added!${okVideos.length ? ' 🎬' : ' 📸'}`);
      if (failed) toast.error(`${failed} item${failed > 1 ? 's' : ''} couldn't be uploaded`);
      await load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Upload failed. Please try again.');
    }
    setUploading(false);
    setVideoProgress(null);
    e.target.value = '';
  };

  const deleteEvent = async () => {
    if (!window.confirm('Delete this event and all its photos?')) return;
    try {
      await axios.delete(`${API_URL}/api/events/${event.id}`, { headers: authHeaders() });
      onDeleted(event.id);
      onClose();
    } catch { /* noop */ }
  };

  return createPortal(
    <AnimatePresence>
      <motion.div key="event-detail-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/90 backdrop-blur-md z-[82]" onClick={onClose} />
      <div key="event-detail-content" className="fixed inset-0 z-[83] flex items-start justify-center p-0 sm:p-4 overflow-y-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 30 }}
          className="w-full max-w-2xl bg-[#12101A] sm:rounded-3xl border border-white/10 min-h-screen sm:min-h-0 sm:my-8 mb-24" data-testid="event-detail-modal">
          <div className="relative">
            <div className="aspect-[16/7] bg-gradient-to-br from-[#FF007F]/30 to-[#00F0FF]/30 overflow-hidden sm:rounded-t-3xl">
              {event.imageUrl && <img src={event.imageUrl} alt={event.title} className="w-full h-full object-cover" />}
            </div>
            <button onClick={onClose} data-testid="close-event-detail" className="absolute top-4 right-4 p-2 rounded-full bg-black/50 hover:bg-black/70">
              <X className="w-5 h-5 text-white" />
            </button>
          </div>
          <div className="p-6">
            <h2 className="text-2xl font-bold text-white" style={{ fontFamily: 'Unbounded' }} data-testid="event-detail-title">{event.title}</h2>
            <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-[#A1A1AA]">
              {event.eventDate && <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4 text-[#00F0FF]" />{formatDate(event.eventDate)}</span>}
              <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#FF007F]" />
                {[event.locationText, event.city, event.state].filter(Boolean).join(', ') || `Zip ${event.zipCode}`}
              </span>
              <span className="flex items-center gap-1.5" data-testid="event-going-count"><Users className="w-4 h-4 text-[#39FF14]" />{rsvpIds.length} going</span>
            </div>
            {event.description && <p className="text-[#A1A1AA] mt-3 leading-relaxed">{event.description}</p>}

            {currentUser && (
              <button onClick={toggleRsvp} disabled={rsvpBusy} data-testid="rsvp-btn"
                className={`mt-4 w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors ${isGoing ? 'bg-[#39FF14] text-[#050505] hover:bg-[#39FF14]/90' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                {rsvpBusy ? <Loader2 className="w-5 h-5 animate-spin" /> : isGoing ? <><Check className="w-5 h-5" /> You're Attending</> : <><CalendarCheck className="w-5 h-5" /> I'm Attending</>}
              </button>
            )}

            {isOwnerOrCreator && (
              <div className="mt-3">
                <button onClick={notifyAttendees} disabled={notifying} data-testid="notify-attendees-btn"
                  className="w-full py-2.5 rounded-xl font-medium flex items-center justify-center gap-2 bg-[#FF007F]/15 text-[#FF007F] hover:bg-[#FF007F]/25 transition-colors">
                  {notifying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />} Notify attendees "Photos are up!"
                </button>
                {notifyMsg && <p className="text-xs text-center text-[#A1A1AA] mt-2" data-testid="notify-result">{notifyMsg}</p>}
              </div>
            )}

            {/* Albums */}
            {(albums.length > 0 || isOwnerOrCreator) && (
              <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="album-bar">
                <button onClick={() => setActiveAlbum('all')} data-testid="album-chip-all"
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${activeAlbum === 'all' ? 'bg-[#00F0FF] text-[#050505]' : 'bg-white/10 text-white/70 hover:text-white'}`}>All</button>
                {albums.map(a => (
                  <button key={a} onClick={() => setActiveAlbum(a)} data-testid={`album-chip-${a}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${activeAlbum === a ? 'bg-[#00F0FF] text-[#050505]' : 'bg-white/10 text-white/70 hover:text-white'}`}>{a}</button>
                ))}
                {isOwnerOrCreator && (
                  <div className="inline-flex items-center gap-1">
                    <input value={newAlbum} onChange={(e) => setNewAlbum(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addAlbum()}
                      placeholder="New album" data-testid="new-album-input"
                      className="w-24 px-2 py-1.5 rounded-full bg-white/5 border border-white/10 text-white text-xs placeholder-white/40 outline-none focus:border-[#00F0FF]/50" />
                    <button onClick={addAlbum} data-testid="add-album-btn" className="p-1.5 rounded-full bg-white/10 hover:bg-white/20 text-white"><FolderPlus className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
            )}

            {/* Media type filter */}
            <div className="mt-5 flex items-center gap-1 p-1 bg-white/5 rounded-full w-fit" data-testid="media-filter-bar">
              {[['all', 'All', Grid3x3], ['image', 'Photos', ImageIcon], ['video', 'Videos', Video]].map(([val, label, Icon]) => (
                <button key={val} onClick={() => setMediaFilter(val)} data-testid={`media-filter-${val}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${mediaFilter === val ? 'bg-[#00F0FF] text-[#050505]' : 'text-white/60 hover:text-white'}`}>
                  <Icon className="w-3.5 h-3.5" /> {label}
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between mt-4 mb-3">
              <h3 className="text-white font-semibold flex items-center gap-2"><ImageIcon className="w-5 h-5 text-[#00F0FF]" />Gallery ({filteredMedia.length})</h3>
              {currentUser && (
                <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#00F0FF] text-[#050505] text-sm font-semibold hover:bg-[#00F0FF]/90 transition-colors" data-testid="add-photos-btn">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add Media
                  <input type="file" accept="image/*,video/*" multiple className="hidden" onChange={handleUpload} disabled={uploading} data-testid="event-photos-input" />
                </label>
              )}
            </div>

            {videoProgress && (
              <div className="mb-3" data-testid="video-upload-progress">
                <div className="flex items-center justify-between text-xs text-white/60 mb-1">
                  <span>Uploading video {videoProgress.index} of {videoProgress.total}…</span>
                  <span>{videoProgress.pct}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-[#00F0FF] transition-all" style={{ width: `${videoProgress.pct}%` }} />
                </div>
              </div>
            )}

            {currentUser && albums.length > 0 && (
              <div className="mb-3 flex items-center gap-2 text-xs text-white/50">
                <span>Upload into:</span>
                <select value={uploadAlbum} onChange={(e) => setUploadAlbum(e.target.value)} data-testid="upload-album-select"
                  className="px-2 py-1 rounded-full bg-white/10 text-white outline-none border border-white/10">
                  <option value="" className="bg-[#12101A]">General</option>
                  {albums.map(a => <option key={a} value={a} className="bg-[#12101A]">{a}</option>)}
                </select>
              </div>
            )}

            {loading ? (
              <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" /></div>
            ) : filteredMedia.length === 0 ? (
              <div className="py-10 text-center text-white/40">
                <ImageIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p>No {mediaFilter === 'video' ? 'videos' : mediaFilter === 'image' ? 'photos' : 'media'} yet. Be the first to share this event!</p>
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-1.5">
                {filteredMedia.map(p => {
                  const isVid = p.media_type === 'video';
                  const rx = totalReactions(p.reactions) + (p.likes || []).length;
                  return (
                    <button key={p.id} onClick={() => onOpenPhoto(p)} className="aspect-square rounded-lg overflow-hidden group relative bg-white/5" data-testid={`album-photo-${p.id}`}>
                      <MediaThumb p={p} isVid={isVid} />
                      {isVid && (
                        <span className="absolute inset-0 flex items-center justify-center">
                          {p.status && p.status !== 'ready'
                            ? <Loader2 className="w-6 h-6 text-white animate-spin" />
                            : <span className="w-9 h-9 rounded-full bg-black/60 flex items-center justify-center"><Play className="w-4 h-4 text-white fill-current ml-0.5" /></span>}
                        </span>
                      )}
                      {rx > 0 && (
                        <span className="absolute bottom-1 left-1 text-[10px] text-white flex items-center gap-0.5 bg-black/50 px-1.5 py-0.5 rounded-full">
                          <Heart className="w-2.5 h-2.5 fill-current" />{rx}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            <ReviewsSection event={event} currentUser={currentUser} isAttending={isGoing} canManage={isOwnerOrCreator} />

            {isOwnerOrCreator && (
              <button onClick={deleteEvent} data-testid="delete-event-btn" className="mt-6 inline-flex items-center gap-1.5 text-sm text-[#FF007F] hover:text-[#FF007F]/80 transition-colors">
                <Trash2 className="w-4 h-4" /> Delete event
              </button>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>,
    document.body
  );
};

const EVENT_CATEGORIES = ['Music', 'Nightlife', 'Show', 'Festival', 'Community', 'Other'];

// ============ Main Page ============
const EventsPage = () => {
  const { user, isAuthenticated } = useAuth();
  const [tab, setTab] = useState('feed'); // 'feed' | 'events'
  const [zip, setZip] = useState('');
  const [appliedZip, setAppliedZip] = useState('');
  const [events, setEvents] = useState([]);
  const [feed, setFeed] = useState([]);
  const [feedMedia, setFeedMedia] = useState('all'); // all | image | video
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewerPhoto, setViewerPhoto] = useState(null);
  // Phase 5 — Event Locator filters
  const [category, setCategory] = useState('');
  const [when, setWhen] = useState('all'); // upcoming | past | all
  const [sort, setSort] = useState('soonest');  // soonest | nearest | newest
  const [coords, setCoords] = useState(null);    // {lat,lng} when "near me" is active
  const [radius, setRadius] = useState('25');
  const [geoBusy, setGeoBusy] = useState(false);

  const canCreate = isAuthenticated && user && (user.role === 'artist' || user.role === 'super_admin' || user.isSuperAdmin);

  const fetchData = useCallback(async (zipCode, filters = {}) => {
    setLoading(true);
    try {
      const ep = new URLSearchParams();
      if (zipCode) ep.append('zipCode', zipCode);
      if (filters.category) ep.append('category', filters.category);
      if (filters.when && filters.when !== 'all') ep.append('when', filters.when);
      if (filters.sort) ep.append('sort', filters.sort);
      if (filters.coords) {
        ep.append('lat', filters.coords.lat);
        ep.append('lng', filters.coords.lng);
        ep.append('radius', filters.radius || '25');
      }
      const feedQuery = new URLSearchParams();
      if (zipCode) feedQuery.append('zipCode', zipCode);
      if (filters.feedMedia && filters.feedMedia !== 'all') feedQuery.append('media_type', filters.feedMedia);
      const feedParams = feedQuery.toString() ? `?${feedQuery.toString()}` : '';
      const [evRes, feedRes] = await Promise.all([
        axios.get(`${API_URL}/api/events?${ep.toString()}`),
        axios.get(`${API_URL}/api/events/gallery/feed${feedParams}`),
      ]);
      setEvents(evRes.data?.events || []);
      setFeed(feedRes.data?.photos || []);
    } catch { /* noop */ }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData(appliedZip, { category, when, sort, coords, radius, feedMedia });
  }, [fetchData, appliedZip, category, when, sort, coords, radius, feedMedia]);

  // Deep-link: open a specific event when arriving via ?event={id} (from shared photo OG links)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const evId = params.get('event');
    if (!evId) return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/events/${evId}`);
        const ev = res.data?.event || res.data;
        if (ev && ev.id) { setSelectedEvent(ev); setTab('events'); }
      } catch { /* event not found */ }
    })();
  }, []);

  const applyZip = () => setAppliedZip(zip.trim());
  const clearZip = () => { setZip(''); setAppliedZip(''); };

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setSort('nearest');
        setGeoBusy(false);
      },
      () => { setGeoBusy(false); },
      { enableHighAccuracy: false, timeout: 8000 }
    );
  };

  const likePhoto = async (photo) => {
    if (!isAuthenticated) return;
    try {
      const res = await axios.post(`${API_URL}/api/events/${photo.event_id}/gallery/${photo.id}/like`, {}, { headers: authHeaders() });
      const liked = res.data?.action === 'liked';
      const updater = (arr) => arr.map(p => p.id === photo.id ? {
        ...p,
        likes: liked ? [...(p.likes || []), user.id] : (p.likes || []).filter(id => id !== user.id)
      } : p);
      setFeed(updater);
      setViewerPhoto(prev => prev && prev.id === photo.id ? {
        ...prev,
        likes: liked ? [...(prev.likes || []), user.id] : (prev.likes || []).filter(id => id !== user.id)
      } : prev);
    } catch { /* noop */ }
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-24" data-testid="events-page">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-[#0B0A10]/95 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-2xl mx-auto px-4 pt-6 pb-3">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>Events</h1>
            {canCreate && (
              <button onClick={() => setShowCreate(true)} data-testid="host-event-btn"
                className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-[#FF007F] text-white text-sm font-semibold hover:bg-[#FF007F]/90 transition-colors">
                <Plus className="w-4 h-4" /> Host
              </button>
            )}
          </div>

          {/* Zip search */}
          <div className="mt-4 flex items-center gap-2">
            <div className="flex-1 flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 border border-white/10 focus-within:border-[#00F0FF]/50">
              <Search className="w-4 h-4 text-white/40" />
              <input value={zip} onChange={(e) => setZip(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && applyZip()}
                placeholder="Search events by zip code" inputMode="numeric"
                className="flex-1 bg-transparent text-white placeholder-white/40 outline-none text-sm" data-testid="events-zip-search" />
              {appliedZip && (
                <button onClick={clearZip} data-testid="clear-zip"><X className="w-4 h-4 text-white/40 hover:text-white" /></button>
              )}
            </div>
            <button onClick={applyZip} data-testid="apply-zip-search"
              className="px-4 py-2.5 rounded-full bg-white/10 text-white text-sm font-medium hover:bg-white/20 transition-colors">Search</button>
          </div>

          {/* Tabs */}
          <div className="mt-4 flex gap-1 p-1 bg-white/5 rounded-full">
            <button onClick={() => setTab('feed')} data-testid="tab-feed"
              className={`flex-1 py-2 rounded-full text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${tab === 'feed' ? 'bg-[#00F0FF] text-[#050505]' : 'text-white/60 hover:text-white'}`}>
              <Grid3x3 className="w-4 h-4" /> Media Feed
            </button>
            <button onClick={() => setTab('events')} data-testid="tab-events"
              className={`flex-1 py-2 rounded-full text-sm font-medium flex items-center justify-center gap-1.5 transition-colors ${tab === 'events' ? 'bg-[#00F0FF] text-[#050505]' : 'text-white/60 hover:text-white'}`}>
              <Calendar className="w-4 h-4" /> Events
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-5">
        {appliedZip && (
          <p className="text-sm text-[#A1A1AA] mb-4" data-testid="zip-filter-label">Showing results near <span className="text-[#00F0FF] font-medium">{appliedZip}</span></p>
        )}

        {/* Event Locator filters (Events tab only) */}
        {tab === 'events' && (
          <div className="mb-5 space-y-3" data-testid="event-filters">
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={coords ? () => { setCoords(null); setSort('soonest'); } : useMyLocation} disabled={geoBusy}
                data-testid="near-me-btn"
                className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-sm font-medium transition-colors ${coords ? 'bg-[#39FF14] text-[#050505]' : 'bg-white/10 text-white hover:bg-white/20'}`}>
                {geoBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Navigation className="w-4 h-4" />}
                {coords ? 'Near Me: On' : 'Near Me'}
              </button>
              {coords && (
                <select value={radius} onChange={(e) => setRadius(e.target.value)} data-testid="radius-select"
                  className="px-3 py-2 rounded-full bg-white/10 text-white text-sm outline-none border border-white/10">
                  {['10', '25', '50', '100'].map(r => <option key={r} value={r} className="bg-[#12101A]">{r} mi</option>)}
                </select>
              )}
              <div className="inline-flex items-center gap-1 text-white/40 text-xs ml-auto"><SlidersHorizontal className="w-3.5 h-3.5" /> Filters</div>
            </div>

            <div className="flex flex-wrap gap-2">
              {/* When */}
              <div className="flex gap-1 p-1 bg-white/5 rounded-full">
                {[['upcoming', 'Upcoming'], ['past', 'Past'], ['all', 'All']].map(([val, label]) => (
                  <button key={val} onClick={() => setWhen(val)} data-testid={`when-${val}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${when === val ? 'bg-[#00F0FF] text-[#050505]' : 'text-white/60 hover:text-white'}`}>
                    {label}
                  </button>
                ))}
              </div>
              {/* Category */}
              <select value={category} onChange={(e) => setCategory(e.target.value)} data-testid="category-select"
                className="px-3 py-1.5 rounded-full bg-white/10 text-white text-xs outline-none border border-white/10">
                <option value="" className="bg-[#12101A]">All categories</option>
                {EVENT_CATEGORIES.map(c => <option key={c} value={c} className="bg-[#12101A]">{c}</option>)}
              </select>
              {/* Sort */}
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select"
                className="px-3 py-1.5 rounded-full bg-white/10 text-white text-xs outline-none border border-white/10">
                <option value="soonest" className="bg-[#12101A]">Soonest</option>
                <option value="nearest" className="bg-[#12101A]">Nearest</option>
                <option value="newest" className="bg-[#12101A]">Newest</option>
              </select>
            </div>
          </div>
        )}

        {/* Feed media filter (Feed tab only) */}
        {tab === 'feed' && (
          <div className="mb-4 flex items-center gap-1 p-1 bg-white/5 rounded-full w-fit" data-testid="feed-media-filter">
            {[['all', 'All', Grid3x3], ['image', 'Photos', ImageIcon], ['video', 'Videos', Video]].map(([val, label, Icon]) => (
              <button key={val} onClick={() => setFeedMedia(val)} data-testid={`feed-media-${val}`}
                className={`px-3 py-1.5 rounded-full text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${feedMedia === val ? 'bg-[#00F0FF] text-[#050505]' : 'text-white/60 hover:text-white'}`}>
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" /></div>
        ) : tab === 'feed' ? (
          feed.length === 0 ? (
            <div className="py-20 text-center text-white/40" data-testid="feed-empty">
              <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg text-white/60">No event {feedMedia === 'video' ? 'videos' : feedMedia === 'image' ? 'photos' : 'media'} yet</p>
              <p className="text-sm mt-1">{appliedZip ? 'Try a different zip code.' : 'Media from events will appear here.'}</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5" data-testid="photo-feed">
              {feed.map(p => {
                const isVid = p.media_type === 'video';
                return (
                  <button key={p.id} onClick={() => setViewerPhoto(p)} className="aspect-square rounded-lg overflow-hidden group relative bg-white/5" data-testid={`feed-photo-${p.id}`}>
                    <MediaThumb p={p} isVid={isVid} />
                    {isVid && (
                      <span className="absolute inset-0 flex items-center justify-center">
                        {p.status && p.status !== 'ready'
                          ? <Loader2 className="w-6 h-6 text-white animate-spin" />
                          : <span className="w-9 h-9 rounded-full bg-black/60 flex items-center justify-center"><Play className="w-4 h-4 text-white fill-current ml-0.5" /></span>}
                      </span>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-2">
                      <span className="text-[10px] text-white truncate">{p.event_title}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )
        ) : (
          events.length === 0 ? (
            <div className="py-20 text-center text-white/40" data-testid="events-empty">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg text-white/60">No events found</p>
              <p className="text-sm mt-1">{appliedZip ? 'Try a different zip code.' : (canCreate ? 'Host the first event!' : 'Check back soon for upcoming events.')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="events-grid">
              {events.map(ev => (
                <motion.button key={ev.id} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
                  onClick={() => setSelectedEvent(ev)} data-testid={`event-card-${ev.id}`}
                  className="text-left bg-[#12101A] rounded-2xl overflow-hidden border border-white/5 hover:border-white/15 transition-colors">
                  <div className="aspect-[16/9] bg-gradient-to-br from-[#FF007F]/30 to-[#00F0FF]/30 overflow-hidden relative">
                    {ev.imageUrl && <img src={ev.imageUrl} alt={ev.title} className="w-full h-full object-cover" />}
                    {ev.eventDate && (
                      <span className="absolute top-2 left-2 px-2 py-1 rounded-full bg-black/60 text-white text-xs font-medium flex items-center gap-1">
                        <Calendar className="w-3 h-3 text-[#00F0FF]" />{formatDate(ev.eventDate)}
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-white font-semibold truncate">{ev.title}</h3>
                    <p className="text-[#71717A] text-sm mt-1 flex items-center gap-1.5 truncate">
                      <MapPin className="w-3.5 h-3.5 text-[#FF007F] shrink-0" />
                      {[ev.locationText, ev.city, ev.state].filter(Boolean).join(', ') || `Zip ${ev.zipCode}`}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-[#52525B] text-xs">
                      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{(ev.rsvpUserIds || []).length} going</span>
                      {typeof ev.distance === 'number' && <span className="flex items-center gap-1 text-[#39FF14]"><Navigation className="w-3 h-3" />{ev.distance} mi</span>}
                      {ev.category && <span className="px-1.5 py-0.5 rounded-full bg-white/5">{ev.category}</span>}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>
          )
        )}
      </div>

      <Footer />

      <CreateEventModal open={showCreate} onClose={() => setShowCreate(false)}
        onCreated={(ev) => { setEvents(prev => [ev, ...prev]); setTab('events'); }} />

      {selectedEvent && (
        <EventDetailModal event={selectedEvent} currentUser={isAuthenticated ? user : null}
          onClose={() => { setSelectedEvent(null); fetchData(appliedZip, { category, when, sort, coords, radius, feedMedia }); }}
          onOpenPhoto={(p) => setViewerPhoto(p)}
          onRsvpChange={(id, ids) => setEvents(prev => prev.map(e => e.id === id ? { ...e, rsvpUserIds: ids } : e))}
          onDeleted={(id) => setEvents(prev => prev.filter(e => e.id !== id))} />
      )}

      {viewerPhoto && (
        <PhotoViewer
          photo={viewerPhoto}
          onClose={() => setViewerPhoto(null)}
          currentUser={isAuthenticated ? user : null}
          canManage={!!user && (user.role === 'super_admin' || user.isSuperAdmin || (selectedEvent && selectedEvent.id === viewerPhoto.event_id && selectedEvent.userId === user.id))}
          onDeleted={(id) => setFeed(prev => prev.filter(p => p.id !== id))}
          onReacted={(id, reactions) => setFeed(prev => prev.map(p => p.id === id ? { ...p, reactions } : p))}
        />
      )}
    </div>
  );
};

export default EventsPage;
